import type { CSSProperties } from "react";
import {
  COLOR_BACKDROP,
  COLOR_BORDER_STRONG,
  COLOR_SURFACE,
  COLOR_TEXT,
  FONT_SIZE_SMALL,
  RADIUS_PANEL,
  RADIUS_SM,
  RADIUS_TOOLBAR,
  Z_MODAL,
} from "./tokens.ts";

/** Shared glassmorphism style for overlay panels. */
export const GLASS_PANEL = {
  background: "rgba(38,38,42,0.85)",
  backdropFilter: "blur(48px) saturate(1.6)",
  WebkitBackdropFilter: "blur(48px) saturate(1.6)",
  borderRadius: RADIUS_PANEL,
  border: `0.5px solid ${COLOR_BORDER_STRONG}`,
} as const;

/** Glassmorphism style for the bottom toolbar (slightly lighter, more rounded). */
export const GLASS_TOOLBAR = {
  background: "rgba(58,58,65,0.65)",
  backdropFilter: "blur(48px) saturate(1.6)",
  WebkitBackdropFilter: "blur(48px) saturate(1.6)",
  borderRadius: RADIUS_TOOLBAR,
  border: `0.5px solid ${COLOR_BORDER_STRONG}`,
} as const;

/** Glassmorphism style for modal dialogs (higher opacity for readability). */
export const GLASS_MODAL = {
  ...GLASS_PANEL,
  background: "rgba(30,30,34,0.95)",
} as const;

/** Full-screen backdrop behind modals. */
export const MODAL_BACKDROP_STYLE: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: COLOR_BACKDROP,
  zIndex: Z_MODAL - 1,
};

/** Centred modal shell — spread this then add width and children. */
export const MODAL_SHELL_STYLE: CSSProperties = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxWidth: "calc(100vw - 40px)",
  ...GLASS_MODAL,
  zIndex: Z_MODAL,
  padding: "20px 24px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

// ---------------------------------------------------------------------------
// Shared control styles — used by library panels and other list UIs
// ---------------------------------------------------------------------------

/** Standard text input inside panels (name fields, search, etc.). */
export const PANEL_INPUT_STYLE: CSSProperties = {
  flex: 1,
  padding: "5px 8px",
  borderRadius: RADIUS_SM,
  border: `0.5px solid ${COLOR_BORDER_STRONG}`,
  background: COLOR_SURFACE,
  color: COLOR_TEXT,
  fontSize: FONT_SIZE_SMALL,
  outline: "none",
};

/** Minimal icon-only button (close, delete, menu dots, etc.). */
export const ICON_BUTTON_STYLE: CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: 4,
  borderRadius: RADIUS_SM,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  minWidth: 24,
  minHeight: 24,
};
